import { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, fmtDate } from '../utils/api';

function validatePassword(password) {
  if (!password || password.length < 10) return 'Password must be at least 10 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
  return null;
}

function getMyRole() {
  try {
    const token = localStorage.getItem('awwt_token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).role || null;
  } catch { return null; }
}

export default function AdminUsers() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);
  const [selected, setSelected] = useState(null);
  const [msg, setMsg]           = useState('');
  const [pageError, setPageError] = useState('');
  const [form, setForm]         = useState({
    email: '', password: '', confirmPassword: '', role: 'vendor',
    firstName: '', lastName: '', companyName: '', phone: ''
  });
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const myRole = getMyRole();

  useEffect(() => { load(); }, []);

  function load() {
    setLoading(true);
    setPageError('');
    apiGet('/users')
      .then(data => {
        console.log('Users loaded:', data);
        setUsers(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Failed to load users:', err);
        setPageError(err.message || 'Failed to load users');
      })
      .finally(() => setLoading(false));
  }

  function openCreate() {
    setForm({ email: '', password: '', confirmPassword: '', role: 'vendor', firstName: '', lastName: '', companyName: '', phone: '' });
    setError('');
    setModal('create');
  }

  function openEdit(user) {
    setSelected(user);
    setForm({
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      companyName: user.company_name || '',
      phone: user.phone || '',
      role: user.role,
      isActive: user.is_active,
    });
    setError('');
    setModal('edit');
  }

  function openPassword(user) {
    setSelected(user);
    setPwForm({ password: '', confirm: '' });
    setError('');
    setModal('password');
  }

  async function handleImpersonate(user) {
    try {
      const data = await apiPost(`/profiles/impersonate/${user.id}`, {});
      sessionStorage.setItem('awwt_imp_token', data.token);
      sessionStorage.setItem('awwt_imp_company', data.user.company_name || user.company_name || '');
      sessionStorage.setItem('awwt_imp_email', data.user.email || user.email || '');
      sessionStorage.setItem('awwt_imp_name', `${data.user.first_name || ''} ${data.user.last_name || ''}`.trim());
      window.location.href = '/vendor';
    } catch (err) {
      setPageError(err.message || 'Failed to start impersonation');
    }
  }

  async function handleCreate() {
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const pwError = validatePassword(form.password);
    if (pwError) {
      setError(pwError);
      return;
    }
    setSaving(true);
    try {
      const { confirmPassword, ...payload } = form;
      await apiPost('/users', payload);
      setMsg('User created successfully');
      setModal(null);
      load();
      setTimeout(() => setMsg(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally { setSaving(false); }
  }

  async function handleEdit() {
    setError(''); setSaving(true);
    try {
      await apiPut(`/users/${selected.id}`, form);
      setMsg('User updated successfully');
      setModal(null);
      load();
      setTimeout(() => setMsg(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally { setSaving(false); }
  }

  async function handlePassword() {
    if (pwForm.password !== pwForm.confirm) {
      setError('Passwords do not match'); return;
    }
    const pwError = validatePassword(pwForm.password);
    if (pwError) {
      setError(pwError); return;
    }
    setError(''); setSaving(true);
    try {
      await apiPost(`/users/${selected.id}/reset-password`, { password: pwForm.password });
      setMsg('Password reset successfully');
      setModal(null);
      setTimeout(() => setMsg(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally { setSaving(false); }
  }

  async function toggleActive(user) {
    try {
      await apiPut(`/users/${user.id}`, { isActive: !user.is_active });
      load();
    } catch (err) {
      console.error(err);
      setPageError(err.message);
    }
  }

  const set = k => e => setForm(f => ({
    ...f,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
  }));

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>User Management</h1>
          <p>Manage vendor and admin accounts</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Add User</button>
      </div>
      {msg && <div className="alert alert-success" style={{ marginBottom: 16 }}>{msg}</div>}
      {pageError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{pageError}</div>}
      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="icon">👤</div>
            <p>No users found.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Role</th>
                <th>Profiles</th>
                <th>Last Login</th>
                <th>Status</th>
                <th></th>
              </tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td><strong>{u.first_name} {u.last_name}</strong></td>
                    <td className="text-muted">{u.email}</td>
                    <td>{u.company_name || '—'}</td>
                    <td>
                      <span className={`badge ${u.role === 'admin' || u.role === 'superadmin' ? 'badge-approved' : 'badge-draft'}`}
                        style={{ textTransform: 'capitalize' }}>
                        {u.role}
                      </span>
                    </td>
                    <td className="text-muted">{u.profile_count || 0}</td>
                    <td className="text-muted">{fmtDate(u.last_login)}</td>
                    <td>
                      <span className={`badge ${u.is_active ? 'badge-approved' : 'badge-rejected'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group">
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>Edit</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => openPassword(u)}>Password</button>
                        <button
                          className={`btn btn-sm ${u.is_active ? 'btn-warn' : 'btn-success'}`}
                          onClick={() => toggleActive(u)}>
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        {myRole === 'superadmin' && u.role === 'vendor' && (
                          <button
                            className="btn btn-sm"
                            style={{ background: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' }}
                            onClick={() => handleImpersonate(u)}>
                            👁 View as
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--ink-lt)', borderTop: '1px solid var(--border)' }}>
              {users.length} user{users.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
      {/* ── Create Modal ───────────────────────────────── */}
      {modal === 'create' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
            <h2>Add New User</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-grid form-grid-2" style={{ marginBottom: 12 }}>
              <div className="form-group">
                <label>First Name</label>
                <input value={form.firstName} onChange={set('firstName')} />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input value={form.lastName} onChange={set('lastName')} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Company Name</label>
              <input value={form.companyName} onChange={set('companyName')} />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Email Address <span className="req">*</span></label>
              <input type="email" value={form.email} onChange={set('email')} />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Phone</label>
              <input type="tel" value={form.phone} onChange={set('phone')} />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Role <span className="req">*</span></label>
              <select value={form.role} onChange={set('role')}>
                <option value="vendor">Vendor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Password <span className="req">*</span></label>
              <input type="password" value={form.password} onChange={set('password')} />
              <span className="form-hint">Minimum 10 characters, with at least one uppercase letter and one number</span>
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Confirm Password <span className="req">*</span></label>
              <input type="password" value={form.confirmPassword} onChange={set('confirmPassword')} />
            </div>
            <div className="btn-group">
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating…' : 'Create User'}
              </button>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Edit Modal ─────────────────────────────────── */}
      {modal === 'edit' && selected && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
            <h2>Edit User</h2>
            <p style={{ marginBottom: 16 }}>{selected.email}</p>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-grid form-grid-2" style={{ marginBottom: 12 }}>
              <div className="form-group">
                <label>First Name</label>
                <input value={form.firstName} onChange={set('firstName')} />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input value={form.lastName} onChange={set('lastName')} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Company Name</label>
              <input value={form.companyName} onChange={set('companyName')} />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Phone</label>
              <input type="tel" value={form.phone} onChange={set('phone')} />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Role</label>
              <select value={form.role} onChange={set('role')}>
                <option value="vendor">Vendor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="checkbox-group">
                <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
                Account is active
              </label>
            </div>
            <div className="btn-group">
              <button className="btn btn-primary" onClick={handleEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Password Modal ─────────────────────────────── */}
      {modal === 'password' && selected && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Reset Password</h2>
            <p style={{ marginBottom: 16 }}>Setting new password for <strong>{selected.email}</strong></p>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>New Password</label>
              <input type="password" value={pwForm.password}
                onChange={e => setPwForm(f => ({ ...f, password: e.target.value }))} />
              <span className="form-hint">Minimum 10 characters, with at least one uppercase letter and one number</span>
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Confirm Password</label>
              <input type="password" value={pwForm.confirm}
                onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} />
            </div>
            <div className="btn-group">
              <button className="btn btn-primary" onClick={handlePassword} disabled={saving}>
                {saving ? 'Saving…' : 'Reset Password'}
              </button>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
